import { db } from '@/lib/db/index';
import type { PhotoStaging } from '@/lib/db/index';
import { enqueue } from '@/lib/sync/queue';
import { nowServer } from '@/lib/sync/serverTime';

export type { PhotoStaging };

/**
 * Stage a photo for offline storage. Returns a local object URL for immediate
 * display in the UI. The sync engine uploads the Blob to Supabase Storage
 * when connectivity returns.
 */
export async function stagePhoto(
  woId: string,
  file: File,
  orgId: string,
  userId: string,
): Promise<{ localUrl: string; stagingId: string }> {
  const stagingId = crypto.randomUUID();
  const now = nowServer();

  const staged: PhotoStaging = {
    id: stagingId,
    wo_id: woId,
    blob: file,
    filename: file.name,
    mime_type: file.type,
    size: file.size,
    staged_at: now,
    upload_status: 'pending',
  };

  await db.photo_stage.add(staged);

  // Enqueue for sync — engine.ts handles the actual Storage upload
  await enqueue({
    type: 'wo:attach_photo',
    entity_id: woId,
    payload: { staged_photo_id: stagingId, filename: file.name, wo_id: woId },
    org_id: orgId,
    user_id: userId,
    optimistic_updated_at: now,
    created_at: now,
    status: 'pending',
    retry_count: 0,
  });

  const localUrl = URL.createObjectURL(file);
  return { localUrl, stagingId };
}

/** Get all staged (not-yet-uploaded) photos for a work order. */
export async function getStagedPhotos(woId: string): Promise<PhotoStaging[]> {
  try {
    return db.photo_stage
      .where('wo_id')
      .equals(woId)
      .filter(p => p.upload_status !== 'uploaded')
      .toArray();
  } catch {
    return [];
  }
}

/** Mark a staged photo as uploaded and store its remote URL. */
export async function markPhotoUploaded(id: string, remoteUrl: string): Promise<void> {
  await db.photo_stage.update(id, { upload_status: 'uploaded', remote_url: remoteUrl });
}

/** Mark a staged photo upload as failed. */
export async function markPhotoFailed(id: string): Promise<void> {
  await db.photo_stage.update(id, { upload_status: 'failed' });
}
